'use client'

import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid'
import { type ComponentPropsWithRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { TextField } from '@src/components/TextField/TextField'
import {
  MIN_8_MAX_20_CHARS,
  NO_BANNED_CHARS,
  ONE_LOWER,
  ONE_NUMBER,
  ONE_UPPER,
  type Schema,
} from '@src/constants/SignUpSchema'
import { useLocale } from '@src/i18n/LocaleProvider'
import { cn } from '@src/utils/tailwind'

import { PasswordRule } from './PasswordRule'

type PasswordFieldProps = ComponentPropsWithRef<'input'> & {
  ruleText: {
    ruleMinEightMaxTwentyChars: string
    ruleOneLower: string
    ruleOneNumber: string
    ruleOneUpper: string
    ruleNoBannedChars: string
  }
}

export const PasswordField = (props: PasswordFieldProps) => {
  const { t: ts } = useLocale('sign-up-form')
  const { t } = useLocale('sign-up-form-errors')
  const {
    watch,
    register,
    formState: { errors },
  } = useFormContext<Schema>()
  const password = watch('password') ?? ''

  const errorMessage = errors.password?.message
  const hasErrors = !!errorMessage

  const {
    ruleText: {
      ruleMinEightMaxTwentyChars,
      ruleOneLower,
      ruleOneNumber,
      ruleOneUpper,
      ruleNoBannedChars,
    },
    ...rest
  } = props

  const [isPasswordVisible, setPasswordVisible] = useState(false)

  const IconRight = isPasswordVisible ? EyeSlashIcon : EyeIcon

  return (
    <div className={cn('w-full pt-4', { 'opacity-20': props.disabled })}>
      <TextField
        type={isPasswordVisible ? 'text' : 'password'}
        id="password"
        data-testid="password"
        labelText={ts('password')}
        {...register('password', { shouldUnregister: false })}
        iconRight={
          <IconRight
            className="cursor-pointer"
            onClick={() => {
              setPasswordVisible(!isPasswordVisible)
            }}
          />
        }
        isInvalid={!!errorMessage}
        captionText={t(errorMessage)}
        {...rest}
      />
      <div className="mt-4 flex flex-col gap-1">
        {[
          [MIN_8_MAX_20_CHARS.test(password), ruleMinEightMaxTwentyChars],
          [ONE_UPPER.test(password), ruleOneUpper],
          [ONE_LOWER.test(password), ruleOneLower],
          [ONE_NUMBER.test(password), ruleOneNumber],
          [NO_BANNED_CHARS.test(password), ruleNoBannedChars],
        ].map(([isValid, content]) => (
          <PasswordRule
            key={content as string}
            isValid={!!password && (isValid as boolean)}
            hasErrors={hasErrors}
            content={content as string}
          />
        ))}
      </div>
    </div>
  )
}
